import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import { ApiFetchError, createApiClient, type ApiClient } from "../lib/api.js";
import {
  getIdToken,
  login as firebaseLogin,
  logout as firebaseLogout,
  observeAuth,
  type FirebaseUser
} from "../lib/auth.js";
import type { MeUser } from "../types.js";

export type AuthStatus =
  | "loading"
  | "anonymous"
  | "authorized"
  | "not_registered"
  | "error";

export interface AuthContextValue {
  status: AuthStatus;
  me: MeUser | null;
  errorMessage: string | null;
  api: ApiClient;
  login: (email: string, senha: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

type MeState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ok"; me: MeUser }
  | { kind: "forbidden"; message: string }
  | { kind: "error"; message: string };

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const api = useMemo<ApiClient>(
    () => createApiClient({ getToken: getIdToken }),
    []
  );
  const [authResolved, setAuthResolved] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [meState, setMeState] = useState<MeState>({ kind: "idle" });

  useEffect(() => {
    return observeAuth((user) => {
      setFirebaseUser(user);
      setAuthResolved(true);
    });
  }, []);

  useEffect(() => {
    if (firebaseUser === null) {
      setMeState({ kind: "idle" });
      return;
    }

    let cancelled = false;
    setMeState({ kind: "loading" });
    api
      .getMe()
      .then((response) => {
        if (!cancelled) {
          setMeState({ kind: "ok", me: response.user });
        }
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        // Distingue "autenticado no Firebase" de "autorizado no FiCC": um e-mail
        // válido fora do allowlist `users` (ou inativo) retorna 403 — estado
        // explícito, nunca um spinner infinito.
        if (error instanceof ApiFetchError && error.status === 403) {
          setMeState({ kind: "forbidden", message: error.message });
        } else {
          const message =
            error instanceof Error
              ? error.message
              : "Não foi possível carregar o seu perfil.";
          setMeState({ kind: "error", message });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, firebaseUser]);

  const login = useCallback(async (email: string, senha: string) => {
    await firebaseLogin(email, senha);
  }, []);

  const logout = useCallback(async () => {
    await firebaseLogout();
    setMeState({ kind: "idle" });
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    return {
      status: deriveStatus(authResolved, firebaseUser, meState),
      me: meState.kind === "ok" ? meState.me : null,
      errorMessage:
        meState.kind === "forbidden" || meState.kind === "error"
          ? meState.message
          : null,
      api,
      login,
      logout
    };
  }, [api, authResolved, firebaseUser, login, logout, meState]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function deriveStatus(
  authResolved: boolean,
  firebaseUser: FirebaseUser | null,
  meState: MeState
): AuthStatus {
  if (!authResolved) {
    return "loading";
  }
  if (firebaseUser === null) {
    return "anonymous";
  }
  switch (meState.kind) {
    case "ok":
      return "authorized";
    case "forbidden":
      return "not_registered";
    case "error":
      return "error";
    case "idle":
    case "loading":
      return "loading";
  }
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error("useAuth deve ser usado dentro de <AuthProvider>.");
  }
  return value;
}
